<!-- The HUD over the Play canvas: countdown ring top-centre, score and Merry
     Multiplier ribbon top-left, Good Energy Meter top-right, the challenge
     target line, and the transient Release / Burst buttons. All of it is DOM,
     so it scales with the reading size and reads to a screen reader. -->
<template>
  <div class="hud" aria-live="off">
    <div class="hud__score">
      <div class="hud__score-value tnum" aria-label="Score">{{ formatted }}</div>
      <div
        :key="pulseKey"
        class="hud__ribbon"
        :class="{ 'hud__ribbon--pulse': pulseKey > 0, 'hud__ribbon--x1': multiplier <= 1 }"
        aria-label="Merry Multiplier"
      >
        <span aria-hidden="true">×{{ multiplier }}</span>
        <span class="sr-only">times {{ multiplier }}</span>
      </div>
    </div>

    <div class="hud__timer" :class="{ 'hud__timer--low': seconds <= 10 }" role="timer" aria-label="Time left">
      <svg viewBox="0 0 56 56" aria-hidden="true">
        <circle class="hud__timer-track" cx="28" cy="28" r="24" />
        <circle
          class="hud__timer-arc"
          cx="28" cy="28" r="24"
          :stroke-dasharray="CIRC"
          :stroke-dashoffset="CIRC * (1 - fraction)"
        />
      </svg>
      <span class="hud__timer-text tnum">{{ seconds }}</span>
    </div>

    <div v-if="showMeter" class="hud__meter" :class="{ 'hud__meter--full': meter >= 1 }" role="meter" :aria-valuenow="Math.round(meter * 100)" aria-valuemin="0" aria-valuemax="100" aria-label="Good Energy Meter">
      <span class="hud__meter-label">Good energy</span>
      <div class="hud__meter-bar"><div class="hud__meter-fill" :style="{ width: `${Math.round(meter * 100)}%` }" /></div>
    </div>

    <div v-if="target" class="hud__chip">Beat {{ target }} {{ target === 1 ? 'home' : 'homes' }}</div>

    <div class="hud__actions">
      <IonButton v-if="showRelease" class="hud__big" @click="$emit('release')">Release</IonButton>
      <IonButton v-if="showBurst" class="hud__big hud__big--burst" @click="$emit('burst')">Burst</IonButton>
    </div>

    <!-- The round's only way out: pause, and the pause card offers Back to
         street. DESIGN.md asks Play to keep a small back affordance, and this
         is it. -->
    <IonButton class="hud__pause" aria-label="Pause the round" @click="$emit('pause')">
      <IonIcon slot="icon-only" :icon="pauseOutline" aria-hidden="true" />
    </IonButton>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { IonButton, IonIcon } from '@ionic/vue';
import { pauseOutline } from 'ionicons/icons';

const props = defineProps({
  timeLeftMs: { type: Number, default: 45000 },
  durationMs: { type: Number, default: 45000 },
  score: { type: Number, default: 0 },
  multiplier: { type: Number, default: 1 },
  meter: { type: Number, default: 0 },
  showMeter: { type: Boolean, default: true },
  target: { type: Number, default: 0 },
  showRelease: { type: Boolean, default: false },
  showBurst: { type: Boolean, default: false },
});
defineEmits(['release', 'burst', 'pause']);

const CIRC = 2 * Math.PI * 24;
const seconds = computed(() => Math.ceil(props.timeLeftMs / 1000));
const fraction = computed(() => (props.durationMs ? props.timeLeftMs / props.durationMs : 0));
const formatted = computed(() => new Intl.NumberFormat('en-AU').format(props.score));

// Re-keying the ribbon restarts its pulse animation on every change.
const pulseKey = ref(0);
watch(() => props.multiplier, () => { pulseKey.value += 1; });
</script>
